import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import {
  Desktop24Regular,
  BuildingFactory24Regular,
  Code24Regular,
  Scales24Regular,
  LockClosed24Regular,
  LearningApp24Regular,
  Password24Regular,
  ShieldCheckmark24Regular,
  CloudArrowUp24Regular,
  ArrowSync24Regular,
  PlugDisconnected24Regular,
  Checkmark20Filled,
} from "@fluentui/react-icons";
import * as React from "react";
import {
  fieldFormatSpec,
  inputModeForFormat,
  validateFieldFormat,
} from "@kmvs/forms-core";
import { BRAND, REFINED } from "./tokens";
import type {
  Answers,
  ContactPickerValue,
  FieldDef,
  FieldType,
  FirmaEntry,
  PersonEntry,
  VerantEntry,
} from "./types";
import PersonEntryList from "./PersonEntryList";
import VerantwortlichkeitenList from "./VerantwortlichkeitenList";
import FirmenVerbundList from "./FirmenVerbundList";
import ContactPickerField from "./ContactPickerField";
import DocumentUploadField from "./DocumentUploadField";
import DokumentStatusField from "./DokumentStatusField";
import { BranchenField } from "./BranchenField";
import UnbekannterFeldtyp from "./UnbekannterFeldtyp";
import { PhoneNumberInput } from "../forms/widgets/PhoneNumberInput";

/** Name dieses Renderers in Konsolenmeldungen und Tests. */
export const RENDERER_NAME = "apps/web QuestionField";

/**
 * Die Feldtypen, die dieser Renderer wirklich bedient — Prüfmass für den
 * Abdeckungstest (`QuestionFieldFeldtypen.test.tsx`) gegen `FIELD_TYPES` aus
 * forms-core. Wer einen Feldtyp ergänzt und diesen Renderer vergisst, macht
 * dort einen Test rot, statt still ein Textfeld auszuliefern.
 *
 * `text`, `number` und `date` stehen mit drin, obwohl sie keinen eigenen Zweig
 * haben: sie sind das gemeinsame `<input>` am Ende der Kette. Alles andere
 * muss vorher abgebogen sein.
 */
export const BEHANDELTE_FELDTYPEN: ReadonlySet<FieldType> = new Set<FieldType>([
  "text",
  "number",
  "date",
  "textarea",
  "yesno",
  "select",
  "checkboxes",
  "iconCards",
  "personEntries",
  "verantEntries",
  "firmaEntries",
  "contactPicker",
  "documents",
  "dokumentStatus",
  "branchen",
]);

const ICONS: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
  hardware: Desktop24Regular,
  betrieb: BuildingFactory24Regular,
  software: Code24Regular,
  haftpflicht: Scales24Regular,
  datenschutz: LockClosed24Regular,
  schulung: LearningApp24Regular,
  passwort: Password24Regular,
  antimalware: ShieldCheckmark24Regular,
  backup: CloudArrowUp24Regular,
  patch: ArrowSync24Regular,
  altsystem: PlugDisconnected24Regular,
};

const useStyles = makeStyles({
  label: {
    display: "block",
  },
  /**
   * Umschließt Label + Eingabe + Formathinweis. Der Hinweis darf NICHT im
   * `<label>` stehen — er zählte sonst zum zugänglichen Namen des Feldes.
   */
  fieldWrap: {
    display: "block",
  },
  /**
   * Label bleibt für Screenreader da, verschwindet aber optisch. Gebraucht in
   * Ansichten mit `hideLabel` (die Auswertungstabelle führt den Anzeigebegriff
   * schon als Zeilentitel) bei Feldern, deren Komponente ein eigenes Label
   * mitbringt — ohne Label hätte das Feld sonst keinen zugänglichen Namen.
   */
  visuallyHidden: {
    position: "absolute",
    width: "1px",
    height: "1px",
    ...shorthands.overflow("hidden"),
    clipPath: "inset(50%)",
    whiteSpace: "nowrap",
  },
  labelText: {
    display: "block",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.72rem",
    fontWeight: 600,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: "#5d5664",
    marginBottom: "4px",
  },
  ocrChip: {
    display: "inline-block",
    marginLeft: "8px",
    fontSize: "0.62rem",
    fontWeight: 600,
    letterSpacing: "0.04em",
    textTransform: "none",
    color: "#0E7C3A",
    backgroundColor: "rgba(14,124,58,0.12)",
    ...shorthands.padding("2px", "8px"),
    ...shorthands.borderRadius("999px"),
  },
  input: {
    width: "100%",
    boxSizing: "border-box",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("9px", "12px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    outlineWidth: 0,
    transitionProperty: "border-color, box-shadow",
    transitionDuration: "120ms",
    ":focus": {
      ...shorthands.borderColor(BRAND.purple),
      boxShadow: `0 0 0 3px ${BRAND.purple}33`,
    },
    "::placeholder": {
      color: "#9c95a3",
    },
  },
  textarea: {
    minHeight: "100px",
    resize: "vertical",
  },
  /**
   * Formatverstoß mit `severity: "error"` — formal prüfbar und eindeutig
   * falsch (Telefon, E-Mail, PLZ, Jahr …).
   */
  inputInvalid: {
    ...shorthands.borderColor(REFINED.err),
    backgroundColor: REFINED.errBg,
    ":focus": {
      ...shorthands.borderColor(REFINED.err),
      boxShadow: REFINED.errRing,
    },
  },
  /**
   * Formatverstoß mit `severity: "warning"` — Zahl-, Geld-, Flächen- und
   * HR-Angaben. „1-2 Mio" und „ca. 1960" sind fachlich gemeinte Antworten;
   * sie werden angemerkt, aber nicht als Fehler markiert.
   */
  inputWarn: {
    ...shorthands.borderColor(REFINED.gold),
    backgroundColor: REFINED.goldBg,
    ":focus": {
      ...shorthands.borderColor(REFINED.gold),
      boxShadow: `0 0 0 3px ${REFINED.gold}22`,
    },
  },
  fieldMessage: {
    display: "block",
    marginTop: "5px",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.78rem",
    lineHeight: 1.35,
  },
  fieldMessageError: {
    color: REFINED.err,
  },
  fieldMessageWarn: {
    color: REFINED.goldInk,
  },
  radioRow: {
    display: "flex",
    alignItems: "center",
    columnGap: "20px",
  },
  radioOption: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "6px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
  },
  radioInput: {
    accentColor: BRAND.purple,
    cursor: "pointer",
  },
  checkboxGrid: {
    display: "grid",
    gap: "8px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  checkboxBox: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "8px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    ...shorthands.padding("9px", "12px"),
    ...shorthands.borderRadius("6px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    transitionProperty: "border-color",
    transitionDuration: "120ms",
    ":hover": {
      ...shorthands.borderColor(BRAND.purple),
    },
  },
  iconCardGrid: {
    display: "grid",
    gap: "12px",
    gridTemplateColumns: "1fr",
    "@media (min-width: 640px)": {
      gridTemplateColumns: "1fr 1fr",
    },
    "@media (min-width: 960px)": {
      gridTemplateColumns: "1fr 1fr 1fr",
    },
  },
  iconCard: {
    position: "relative",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
    cursor: "pointer",
    fontFamily: BRAND.fontPrimary,
    color: BRAND.ink,
    backgroundColor: BRAND.white,
    textAlign: "left",
    ...shorthands.padding("14px"),
    ...shorthands.borderRadius("10px"),
    ...shorthands.border("1px", "solid", "#d4cfd9"),
    transitionProperty: "border-color, box-shadow, transform",
    transitionDuration: "150ms",
    ":hover": {
      transform: "translateY(-1px)",
      boxShadow: "0 8px 16px rgba(45,20,43,0.10)",
    },
  },
  iconCardSelected: {
    ...shorthands.borderWidth("2px"),
    boxShadow: "0 6px 14px rgba(45,20,43,0.12)",
  },
  iconCardTop: {
    display: "flex",
    alignItems: "center",
    columnGap: "10px",
  },
  iconCardIcon: {
    width: "40px",
    height: "40px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.white,
    flexShrink: 0,
    ...shorthands.borderRadius("8px"),
  },
  iconCardTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "0.98rem",
    fontWeight: 600,
    lineHeight: 1.2,
    color: BRAND.ink,
  },
  iconCardDesc: {
    fontSize: "0.82rem",
    lineHeight: 1.35,
    color: BRAND.muted,
  },
  iconCardCheck: {
    position: "absolute",
    top: "10px",
    right: "10px",
    width: "22px",
    height: "22px",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    color: BRAND.white,
    ...shorthands.borderRadius("999px"),
  },
});

type Props = {
  field: FieldDef;
  answers: Answers;
  onChange: (key: string, value: unknown) => void;
  /**
   * When true, the field's own `label` is suppressed. Use this in views
   * where the row already shows a heading (e.g. the Auswertung table
   * which uses the Anzeigebegriff as the row label).
   */
  hideLabel?: boolean;
  /**
   * When the field's key is in this set, a small "aus Fahrzeugschein"
   * chip is rendered next to the label. Used by the KFZ step form
   * after an OCR auto-fill so the broker sees which values came from
   * the uploaded Fahrzeugschein and can review them before submitting.
   */
  ocrFilledKeys?: ReadonlySet<string>;
};

const OcrChip = ({ className }: { className: string }) => (
  <span className={className}>· aus Fahrzeugschein</span>
);

const QuestionField = ({ field, answers, onChange, hideLabel, ocrFilledKeys }: Props) => {
  const styles = useStyles();
  // Erst nach dem Verlassen des Feldes wird gemeldet — sonst schreit jede
  // halb getippte Telefonnummer den Nutzer an, während er sie eingibt.
  const [beruehrt, setBeruehrt] = React.useState(false);
  const value = answers[field.key];
  const showLabel = !hideLabel && !!field.label;
  const showOcrChip = ocrFilledKeys?.has(field.key) === true;

  if (field.type === "textarea") {
    return (
      <label className={styles.label}>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <textarea
          className={mergeClasses(styles.input, styles.textarea)}
          value={typeof value === "string" ? value : ""}
          placeholder={field.placeholder}
          onChange={(e) => onChange(field.key, e.target.value)}
        />
      </label>
    );
  }

  if (field.type === "yesno") {
    return (
      <label className={styles.label}>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <div className={styles.radioRow}>
          {[
            { v: "ja", label: "Ja" },
            { v: "nein", label: "Nein" },
          ].map((opt) => (
            <label key={opt.v} className={styles.radioOption}>
              <input
                type="radio"
                className={styles.radioInput}
                checked={value === opt.v}
                onChange={() => onChange(field.key, opt.v)}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </label>
    );
  }

  if (field.type === "select") {
    return (
      <label className={styles.label}>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <select
          className={styles.input}
          value={typeof value === "string" ? value : ""}
          onChange={(e) => onChange(field.key, e.target.value)}
        >
          <option value="">— wählen —</option>
          {(field.options ?? []).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </label>
    );
  }

  if (field.type === "checkboxes") {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (v: string) => {
      const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
      onChange(field.key, next);
    };
    return (
      <div>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <div className={styles.checkboxGrid}>
          {(field.options ?? []).map((o) => (
            <label key={o.value} className={styles.checkboxBox}>
              <input
                type="checkbox"
                className={styles.radioInput}
                checked={arr.includes(o.value)}
                onChange={() => toggle(o.value)}
              />
              {o.label}
            </label>
          ))}
        </div>
      </div>
    );
  }

  if (field.type === "personEntries") {
    return (
      <div>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <PersonEntryList
          field={field}
          value={Array.isArray(value) ? (value as PersonEntry[]) : []}
          onChange={(next) => onChange(field.key, next)}
        />
      </div>
    );
  }

  if (field.type === "verantEntries") {
    return (
      <div>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <VerantwortlichkeitenList
          field={field}
          value={Array.isArray(value) ? (value as VerantEntry[]) : []}
          onChange={(next) => onChange(field.key, next)}
        />
      </div>
    );
  }

  if (field.type === "firmaEntries") {
    return (
      <div>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <FirmenVerbundList
          field={field}
          value={Array.isArray(value) ? (value as FirmaEntry[]) : []}
          onChange={(next) => onChange(field.key, next)}
        />
      </div>
    );
  }

  if (field.type === "contactPicker") {
    const picked =
      value && typeof value === "object" && "contactId" in (value as object)
        ? (value as ContactPickerValue)
        : undefined;
    return (
      <ContactPickerField
        field={field}
        value={picked}
        onChange={(k, v) => onChange(k, v)}
        hideLabel={!showLabel}
      />
    );
  }

  if (field.type === "iconCards") {
    const arr = Array.isArray(value) ? (value as string[]) : [];
    const toggle = (v: string) => {
      const next = arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v];
      onChange(field.key, next);
    };
    return (
      <div>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <div className={styles.iconCardGrid}>
          {(field.options ?? []).map((o) => {
            const selected = arr.includes(o.value);
            const Icon = o.icon ? ICONS[o.icon] : null;
            const accent = o.color ?? BRAND.purple;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => toggle(o.value)}
                className={mergeClasses(styles.iconCard, selected && styles.iconCardSelected)}
                style={selected ? { borderColor: accent } : undefined}
                aria-pressed={selected}
              >
                {selected && (
                  <span className={styles.iconCardCheck} style={{ backgroundColor: accent }}>
                    <Checkmark20Filled />
                  </span>
                )}
                <div className={styles.iconCardTop}>
                  <span className={styles.iconCardIcon} style={{ backgroundColor: accent }}>
                    {Icon ? <Icon /> : null}
                  </span>
                  <span className={styles.iconCardTitle}>{o.label}</span>
                </div>
                {o.description && (
                  <span className={styles.iconCardDesc}>{o.description}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  if (field.type === "documents") {
    return (
      <div>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <DocumentUploadField
          field={field}
          value={value}
          /*
           * Zurückgeschrieben wird ausschliesslich die Liste, die der SERVER
           * geliefert hat — nie ein Dateiinhalt. Ob überhaupt geschrieben
           * werden darf, entscheidet allein das `uploadTarget` (Vorgabe:
           * readonly), nicht das Vorhandensein dieses Rückkanals.
           */
          onChange={(dateien) => onChange(field.key, dateien)}
        />
      </div>
    );
  }

  if (field.type === "branchen") {
    return (
      <div>
        {showLabel && <span className={styles.labelText}>{field.label}</span>}
        <BranchenField
          value={value}
          onChange={(wert) => onChange(field.key, wert)}
          idBase={`analyse-${field.key}`}
        />
      </div>
    );
  }

  if (field.type === "dokumentStatus") {
    return (
      <div>
        {showLabel && <span className={styles.labelText}>{field.label}</span>}
        <DokumentStatusField
          field={field}
          value={value}
          onChange={(wert) => onChange(field.key, wert)}
        />
      </div>
    );
  }

  /**
   * Ab hier bliebe nur noch das gemeinsame `<input>`. Was nicht `text`,
   * `number` oder `date` ist, hat oben einen Zweig verpasst — und darf NICHT
   * still als Textfeld erscheinen: Der Nutzer tippte sonst Text in ein Feld,
   * das etwas ganz anderes erwartet, und niemand merkte es. Der Rückfall ist
   * deshalb sichtbar, meldet sich in der Konsole und lässt den gespeicherten
   * Wert unangetastet — er stürzt aber nicht ab: das Formular muss auch mit
   * einem Feld aus einem neueren Schema ausfüllbar und absendbar bleiben.
   */
  if (field.type !== "text" && field.type !== "number" && field.type !== "date") {
    return <UnbekannterFeldtyp field={field} renderer={RENDERER_NAME} />;
  }

  const text =
    typeof value === "string" || typeof value === "number" ? String(value) : "";

  /**
   * Telefonfelder bekommen die Komponente aus der PO-Spezifikation: Länder-
   * Dropdown *im* Feld, Flagge statt Ländername, führende 0 wird gar nicht
   * angenommen, gespeichert wird „+49 221 12345".
   *
   * Sie bringt ihre eigene Prüfung und ihren eigenen Hinweis mit (sie kennt
   * zusätzlich unerwartete Zeichen) — deshalb hier ein früher Ausstieg statt
   * einer zweiten Meldung aus `validateFieldFormat`.
   */
  if (field.format === "phone") {
    return (
      <div className={styles.fieldWrap}>
        <PhoneNumberInput
          label={
            <>
              {field.label ?? "Telefonnummer"}
              {showOcrChip && <OcrChip className={styles.ocrChip} />}
            </>
          }
          labelClassName={mergeClasses(
            styles.labelText,
            showLabel ? undefined : styles.visuallyHidden,
          )}
          name={field.key}
          value={text}
          placeholder={field.placeholder}
          onChange={(v) => onChange(field.key, v)}
          // Durchwahl „-" nur bei geschäftlichen Nummern — privat ist, was so heißt.
          geschaeftlich={!/privat/i.test(field.key)}
        />
      </div>
    );
  }

  const htmlType =
    field.type === "number" ? "number" : field.type === "date" ? "date" : "text";

  // Ohne `format` ist `spec` undefined und alles Folgende fällt weg — das
  // Feld verhält sich dann exakt wie vor dieser Erweiterung.
  const spec = fieldFormatSpec(field);
  const verstoss = beruehrt ? validateFieldFormat(field, text) : undefined;
  const istFehler = verstoss?.severity === "error";
  const meldungId = verstoss ? `${field.key}-formathinweis` : undefined;

  /**
   * Kanonisieren beim Verlassen — aber **nur, was gültig ist**. Ein Wert, den
   * wir gleichzeitig bemängeln, wird nie umgeschrieben: Der Nutzer soll genau
   * das wiederfinden, was er getippt hat, sonst kann er den Hinweis nicht
   * einordnen. Aus "0221 12345" wird "+49 221 12345", aus "bitte über Frau
   * Meier" bleibt "bitte über Frau Meier" samt Meldung.
   */
  const beimVerlassen = (roh: string) => {
    setBeruehrt(true);
    if (!spec || roh.trim() === "" || !spec.isValid(roh)) return;
    const kanonisch = spec.normalize(roh);
    if (kanonisch !== roh) onChange(field.key, kanonisch);
  };

  // Die Meldung steht bewusst AUSSERHALB des <label>: alles innerhalb eines
  // umschließenden Labels zählt zum zugänglichen Namen des Feldes — der
  // Screenreader würde das Feld sonst „Telefon Bitte eine gültige
  // Telefonnummer eingeben" nennen. Angebunden wird sie über
  // `aria-describedby`. Der Wrapper wird immer gerendert (nicht nur bei einer
  // Meldung), damit React den Baum nicht umhängt und das Feld nicht neu
  // aufbaut, sobald ein Hinweis erscheint.
  return (
    <div className={styles.fieldWrap}>
      <label className={styles.label}>
        {showLabel && (
          <span className={styles.labelText}>
            {field.label}
            {showOcrChip && <OcrChip className={styles.ocrChip} />}
          </span>
        )}
        <input
          type={htmlType}
          inputMode={inputModeForFormat(field.format)}
          className={mergeClasses(
            styles.input,
            istFehler ? styles.inputInvalid : undefined,
            verstoss && !istFehler ? styles.inputWarn : undefined,
          )}
          value={text}
          placeholder={field.placeholder}
          aria-invalid={istFehler || undefined}
          aria-describedby={meldungId}
          onChange={(e) => onChange(field.key, e.target.value)}
          onBlur={(e) => beimVerlassen(e.target.value)}
        />
      </label>
      {verstoss && (
        <span
          id={meldungId}
          role={istFehler ? "alert" : undefined}
          className={mergeClasses(
            styles.fieldMessage,
            istFehler ? styles.fieldMessageError : styles.fieldMessageWarn,
          )}
        >
          {verstoss.message}
        </span>
      )}
    </div>
  );
};

export default QuestionField;
